import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import { DARK_CHROME, LIGHT_CHROME } from 'core/theme.tokens'
import { createAppTheme } from 'core/theme'

export type ColorModePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'theme:v1'

const readPreference = (): ColorModePreference => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // incognito / quota
  }
  return 'light'
}

const writePreference = (value: ColorModePreference) => {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // incognito / quota
  }
}

const systemPrefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches

const resolveMode = (preference: ColorModePreference): 'light' | 'dark' => {
  if (preference === 'system') {
    return systemPrefersDark() ? 'dark' : 'light'
  }
  return preference
}

const syncThemeColor = (mode: 'light' | 'dark') => {
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', mode === 'dark' ? DARK_CHROME : LIGHT_CHROME)
  }
}

interface ColorModeContextValue {
  preference: ColorModePreference
  resolvedMode: 'light' | 'dark'
  setPreference: (value: ColorModePreference) => void
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined)

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ColorModePreference>(readPreference)
  const [systemDark, setSystemDark] = useState(systemPrefersDark)

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => setSystemDark(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const resolvedMode = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference

  useEffect(() => {
    syncThemeColor(resolvedMode)
    document.documentElement.style.colorScheme = resolvedMode
    document.documentElement.setAttribute('data-color-mode', resolvedMode)
  }, [resolvedMode])

  const setPreference = useCallback((value: ColorModePreference) => {
    writePreference(value)
    setPreferenceState(value)
  }, [])

  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode])

  const value = useMemo(
    () => ({ preference, resolvedMode, setPreference }),
    [preference, resolvedMode, setPreference],
  )

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export const useColorMode = (): ColorModeContextValue => {
  const ctx = useContext(ColorModeContext)
  if (!ctx) {
    throw new Error('useColorMode must be used within ColorModeProvider')
  }
  return ctx
}

export { resolveMode }
